import { createI18n } from 'vue-i18n'
import { languages } from '@/config.json'

// Cache for loaded languages to avoid reloading
const loadedLanguages = new Set()

// Get saved locale from localStorage or default to English
function getInitialLocale() {
  const savedLanguageId = localStorage.getItem('language_preference')
  if (savedLanguageId) {
    const language = languages.find((lang) => lang.id === parseInt(savedLanguageId))
    return language ? language.code : 'en'
  }
  return 'en'
}

// Function to dynamically load a language
async function loadLanguage(languageCode) {
  // Skip if already loaded
  if (loadedLanguages.has(languageCode)) {
    return
  }

  try {
    // Dynamic imports for main locale and errors
    const [localeModule, errorsModule] = await Promise.all([
      import(`@/locales/${languageCode}.json`),
      import(`@/locales/errors/${languageCode}.json`).catch(() => {
        // Fallback to English errors if language-specific errors don't exist
        return import('@/locales/errors/en.json')
      }),
    ])

    // Merge main locale with errors
    const messages = {
      ...localeModule.default,
      errors: errorsModule.default,
    }

    // Add messages to i18n instance
    i18nInstance.global.setLocaleMessage(languageCode, messages)
    loadedLanguages.add(languageCode)
  } catch (error) {
    console.error(`Failed to load language ${languageCode}:`, error)
    // If loading fails, ensure English is available as fallback
    if (languageCode !== 'en' && !loadedLanguages.has('en')) {
      await loadLanguage('en')
    }
  }
}

// Initialize with empty messages - languages will be loaded on demand
const messages = {}

// Create a separate i18n instance for use in non-Vue files
const i18nInstance = createI18n({
  legacy: false,
  locale: 'en', // Start with English, will be updated after loading
  fallbackLocale: 'en',
  messages,
})

// Function to change locale with dynamic loading
export async function changeLocale(languageId) {
  const language = languages.find((lang) => lang.id === languageId)
  if (!language) {
    return false
  }

  try {
    // Load the language if not already loaded
    await loadLanguage(language.code)

    // Change the locale
    i18nInstance.global.locale.value = language.code
    localStorage.setItem('language_preference', languageId.toString())
    return true
  } catch (error) {
    console.error('Failed to change locale:', error)
    return false
  }
}

// Function to get current locale info
export function getCurrentLocale() {
  const currentCode = i18nInstance.global.locale.value
  return languages.find((lang) => lang.code === currentCode) || languages[0]
}

// Initialize the app with the correct language
export async function initializeI18n() {
  const initialLocale = getInitialLocale()
  await loadLanguage(initialLocale)
  i18nInstance.global.locale.value = initialLocale
}

// Export the translation function for use in API and store files
export const t = i18nInstance.global.t
export const te = i18nInstance.global.te
export default i18nInstance
